import React, { useState } from 'react';
import { Shield, CheckCircle, Search, ShieldCheck, Lock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { antivirusProducts } from '../mock';

const Antivirus = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: ''
  });

  const filteredProducts = antivirusProducts.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBrand = brandFilter === 'all' || product.brand.toLowerCase() === brandFilter;
    return matchesSearch && matchesBrand;
  });

  const handlePurchase = (product) => {
    setSelectedProduct(product);
    setShowQuoteForm(true);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand: selectedProduct.brand,
          model: selectedProduct.name,
          category: 'Antivirus Software',
          specs: `${selectedProduct.coverage} - ${selectedProduct.duration}`,
          ...quoteFormData
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Purchase Request Submitted!",
          description: data.message || `We'll contact you shortly about ${selectedProduct.name}.`,
        });
        setShowQuoteForm(false);
        setQuoteFormData({ customerName: '', customerEmail: '', customerPhone: '' });
      } else {
        throw new Error(data.detail || 'Failed to submit purchase request');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again or call us directly.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-gradient-to-br from-green-50 via-white to-emerald-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
              Premium Security Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Professional Antivirus Software
              <span className="block text-green-600 mt-2">Protect Your Digital World</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Enterprise-grade protection from $299.99 to $999.99. Comprehensive security solutions for individuals and businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Why Antivirus Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why You Need Premium Antivirus</h2>
            <p className="text-lg text-gray-600">Comprehensive protection against modern cyber threats</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6 group hover:bg-green-50 rounded-lg transition-colors">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-600 transition-colors">
                <ShieldCheck className="h-8 w-8 text-green-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Real-Time Protection</h3>
              <p className="text-sm text-gray-600">24/7 monitoring against viruses, malware, ransomware, and online threats</p>
            </div>

            <div className="text-center p-6 group hover:bg-green-50 rounded-lg transition-colors">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-600 transition-colors">
                <Lock className="h-8 w-8 text-green-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Identity Protection</h3>
              <p className="text-sm text-gray-600">Secure your personal information, passwords, and financial data from theft</p>
            </div>

            <div className="text-center p-6 group hover:bg-green-50 rounded-lg transition-colors">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-600 transition-colors">
                <AlertCircle className="h-8 w-8 text-green-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Advanced Threat Detection</h3>
              <p className="text-sm text-gray-600">AI-powered detection of zero-day threats and emerging malware</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search antivirus software..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  <SelectItem value="norton">Norton</SelectItem>
                  <SelectItem value="mcafee">McAfee</SelectItem>
                  <SelectItem value="kaspersky">Kaspersky</SelectItem>
                  <SelectItem value="bitdefender">Bitdefender</SelectItem>
                  <SelectItem value="eset">ESET</SelectItem>
                  <SelectItem value="trend micro">Trend Micro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
              <div className="relative h-48 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <Badge className="absolute top-4 right-4 bg-green-600 text-white">{product.category}</Badge>
              </div>
              
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardDescription className="text-xs mb-1">{product.brand}</CardDescription>
                    <CardTitle className="text-xl">{product.name}</CardTitle>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-gray-600"><strong>Coverage:</strong> {product.coverage}</p>
                  <p className="text-sm text-gray-600"><strong>Duration:</strong> {product.duration}</p>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {product.features.slice(0, 5).map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-gray-700">{feature}</span>
                    </div>
                  ))}
                  {product.features.length > 5 && (
                    <p className="text-xs text-gray-500 italic">+{product.features.length - 5} more features...</p>
                  )}
                </div>
                
                <div className="pt-4 border-t">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
                    <span className="text-sm text-gray-600 ml-2">one-time</span>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="flex gap-2">
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handlePurchase(product)}>
                  <Shield className="h-4 w-4 mr-2" />
                  Purchase Now
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}

        {/* Installation & Support Section */}
        <section className="mt-20 bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">What's Included</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Free Installation</h3>
              <p className="text-gray-600 text-sm">Professional setup and configuration on all your devices included</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">License Management</h3>
              <p className="text-gray-600 text-sm">We handle all subscription renewals and license transfers</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Ongoing Support</h3>
              <p className="text-gray-600 text-sm">24/7 technical support for any security-related issues</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Need Help Choosing the Right Protection?</h3>
          <p className="mb-6 text-green-100">Our security experts are here to help you find the perfect antivirus solution for your needs.</p>
          <Button size="lg" variant="secondary">
            <Shield className="h-5 w-5 mr-2" />
            Contact Our Security Team
          </Button>
        </section>
      </div>

      {/* Purchase Form Dialog */}
      <Dialog open={showQuoteForm} onOpenChange={setShowQuoteForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase Antivirus Software</DialogTitle>
            <DialogDescription>
              {selectedProduct && `${selectedProduct.name} - $${selectedProduct.price}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuoteSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Your Name</Label>
              <Input
                id="customerName"
                placeholder="John Doe"
                value={quoteFormData.customerName}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerEmail">Email Address</Label>
              <Input
                id="customerEmail"
                type="email"
                placeholder="john@example.com"
                value={quoteFormData.customerEmail}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerEmail: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerPhone">Phone Number</Label>
              <Input
                id="customerPhone"
                type="tel"
                placeholder="(555) 123-4567"
                value={quoteFormData.customerPhone}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerPhone: e.target.value })}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowQuoteForm(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">Submit Purchase Request</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Antivirus;
